import { RSAAAction } from 'redux-api-middleware'
import type { ThunkAction } from '../types'
import { RootState } from '../../reducers'
import getChunkUploadUrl, { GetChunkURLAction, GetChunkURLRSAAAction } from './getChunkUploadUrl'
import completeMultipartUpload, { CompleteMultipartUploadAction, CompleteMultipartUploadSAAAction } from './completeMultipartUpload'
import ActionTypes from './actionTypes'

type UploadFileStart = {
    type: typeof ActionTypes.UPLOAD_FILE_START;
    payload: never;
}

type UploadFileError = {
    type: typeof ActionTypes.UPLOAD_FILE_ERROR;
    payload: Error;
}

type UploadFileCompleted = {
    type: typeof ActionTypes.UPLOAD_FILE_COMPLETED;
    payload: never;
}

export type UploadFileV3Action = UploadFileStart | UploadFileError | UploadFileCompleted

type UploadFileV3RSAAAction = RSAAAction<RootState, never, never>
type UploadFileV3 = ThunkAction<
    Promise<UploadFileError | UploadFileCompleted>,
    UploadFileV3Action
        | UploadFileV3RSAAAction
        | GetChunkURLRSAAAction
        | CompleteMultipartUploadSAAAction
>

interface UploadFileV3Params {
    file: File;
    filename: string;
    uploadId: string;
}

interface UploadFileV3Opts {
    basePath?: string;
    onProgress?: (progress: number) => void;
}

function uploadFileV3(
    { file, filename, uploadId }: UploadFileV3Params,
    opts?: UploadFileV3Opts,
): UploadFileV3 {
    const { basePath, onProgress = () => null } = opts ?? {}

    return async (dispatch) => {
        dispatch({ type: ActionTypes.UPLOAD_FILE_START } as UploadFileStart)
  
        try {
            // Step 1: Initiate multipart upload
            onProgress(0)

            // Step 2: Split file into chunks
            const chunkSize = 5 * 1024 * 1024 // 20MB per part
            const totalParts = Math.ceil(file.size / chunkSize)
            const uploadedParts = []

            for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
                const start = (partNumber - 1) * chunkSize
                const end = Math.min(start + chunkSize, file.size)
                const chunk = file.slice(start, end)

                // Step 3: Get pre-signed URL for each part
                const presignPartAction: GetChunkURLAction = await dispatch(
                    getChunkUploadUrl({ filename, partNumber, uploadId }, { basePath }),
                )

                if (presignPartAction.type !== 'GET_CHUNK_URL_SUCCESS') {
                    throw presignPartAction.payload
                }

                const { url: chunkUrl } = presignPartAction.payload

                // Step 4: Upload the chunk
                const uploadResponse = await fetch(chunkUrl, {
                    method: 'PUT',
                    body: chunk,
                    headers: { 'Content-Type': file.type },
                })

                if (!uploadResponse.ok) {
                    throw new Error("Failed to upload part " + partNumber)
                }

                uploadedParts.push({
                    PartNumber: partNumber,
                    ETag: uploadResponse.headers.has('Etag')
                        ? (uploadResponse.headers.get('Etag') as string).replace(/"/g, '')
                        : ''
                })

                onProgress(partNumber / (totalParts + 1))
            }

            // Step 5: Complete multipart upload
            const completeUploadAction: CompleteMultipartUploadAction = await dispatch(
                completeMultipartUpload({
                    filename,
                    parts: uploadedParts,
                    uploadId,
                }, {
                    basePath,
                },
            ))

            onProgress(1)

            if (completeUploadAction.type !== 'COMPLETE_MULTIPART_UPLOAD_SUCCESS') {
                throw completeUploadAction.payload
            }

            return dispatch({ type: ActionTypes.UPLOAD_FILE_COMPLETED } as UploadFileCompleted)
        } catch (error) {
            return dispatch({
                type: ActionTypes.UPLOAD_FILE_ERROR,
                payload: error as Error,
            } as UploadFileError)
        }
    }
}

export { uploadFileV3 }
